import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Copy, ExternalLink, MoreHorizontal, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { dashboardHref, docsHubDocumentHref } from '@atbash-labs/plantview-links'
import {
  useAppDispatch,
  useAppSelector,
  useDeleteChatArtifactMutation,
  useGetChatArtifactQuery,
  useGetDocQuery,
  useListDashboardsQuery,
  type Dashboard,
  type Doc
} from '@axilon-apps/domains-ontology-ui-state'

import { describeEntity, useChatAppContext } from '../../chat-app-context'
import { ChatMarkdown } from '../../components/ChatMarkdown'
import { readMutationError } from '../../components/explorer/mutation-error'
import SandboxFrame from '../../components/DashboardSandbox/SandboxFrame'
import { useSandboxTemplateRegistry } from '../../components/DashboardSandbox/useSandboxTemplateRegistry'
import { chatFullPagePath } from '../../components/Chat/chat-route'
import { writeClipboardPayload } from '../../components/Chat/copyRenderedMarkdown'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu'
import { cn } from '../../lib/utils'
import DocFilePreview from '../DocsHubPage/DocFilePreview'
import { ARTIFACT_PAGE_MEASURE } from './artifact-measure'
import { ARTIFACT_BADGE_LABEL } from './artifact-strings'
import { DELETED_DOCUMENT_MESSAGE, documentArtifactBody } from './document-artifact'
import DocumentArtifactDownloadButton from './DocumentArtifactDownloadButton'
import { draftMakeLiveTurn } from './make-live'
import SaveToDashboardsDialog from './SaveToDashboardsDialog'
import SaveToDocsHubDialog from './SaveToDocsHubDialog'

export default function ArtifactPage() {
  const { artifactId } = useParams<{ artifactId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [saveOpen, setSaveOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteChatArtifact] = useDeleteChatArtifactMutation()
  const artifactQuery = useGetChatArtifactQuery(artifactId ?? '', {
    skip: artifactId === undefined
  })
  const dashboardsQuery = useListDashboardsQuery()
  const templateRegistry = useSandboxTemplateRegistry()
  const artifact = artifactQuery.data
  const canMakeLive = useAppSelector((state) => {
    if (!artifact) return true
    return state.agentOrchestrator.tabs[artifact.conversation_id]?.canContribute !== false
  })
  const exportedDashboardId = artifact?.exported_dashboard_id
  const hasExportedDashboard = exportedDashboardId !== null && exportedDashboardId !== undefined
  const exportedDashboard = hasExportedDashboard
    ? dashboardsQuery.data?.find((dashboard) => dashboard.id === exportedDashboardId)
    : undefined
  const isDocument = artifact?.kind === 'document'
  const exportedDocumentId = artifact?.exported_document_id
  const hasExportedDocument = exportedDocumentId !== null && exportedDocumentId !== undefined
  const exportedDocQuery = useGetDocQuery(exportedDocumentId ?? '', {
    skip: !hasExportedDocument
  })
  const exportedDoc = hasExportedDocument ? exportedDocQuery.data : undefined
  const appContext = useMemo(
    () =>
      artifact
        ? [
            { key: 'View', value: 'artifact' },
            {
              key: 'Open artifact',
              value: describeEntity({
                name: artifact.title,
                fields: [
                  ['conversation', artifact.conversation_id],
                  artifact.kind === 'document'
                    ? ['kind', artifact.kind]
                    : ['fetch mode', artifact.fetch_mode]
                ],
                id: artifact.id
              })
            }
          ]
        : undefined,
    [artifact]
  )
  useChatAppContext(appContext)

  if (!artifact) {
    if (artifactId !== undefined && !artifactQuery.isError) {
      return <div className="p-6 text-sm text-muted-foreground">Loading artifact…</div>
    }
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md rounded-lg border border-line bg-card p-6 text-center">
          <h1 className="text-lg font-semibold text-ink">This artifact is no longer available</h1>
          <p className="mt-2 text-sm text-ink-3">
            It may have been deleted, or you may not have access to its conversation.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <RouterLink to="/chat">Back to chat</RouterLink>
          </Button>
        </div>
      </div>
    )
  }

  const documentBody = isDocument ? documentArtifactBody(artifact) : null
  const canCopy = !isDocument || documentBody?.kind === 'markdown'
  const copyLabel = isDocument ? 'markdown' : 'code'
  const hasExport = isDocument ? hasExportedDocument : hasExportedDashboard
  const exportedLink = isDocument
    ? exportedDoc && { href: docsHubDocumentHref(exportedDoc.path), label: 'Open document' }
    : exportedDashboard && {
        href: dashboardHref(exportedDashboard.path),
        label: 'Open dashboard'
      }

  const copyCode = async () => {
    try {
      await writeClipboardPayload({ text: artifact.code })
      toast(isDocument ? 'Markdown copied' : 'Code copied')
    } catch {
      toast.error(`Could not copy ${copyLabel}`)
    }
  }

  const handleSaved = (dashboard: Dashboard) => {
    toast('Saved to Dashboards', {
      description: `/${dashboard.path}`,
      action: {
        label: 'Open dashboard',
        onClick: () => navigate(dashboardHref(dashboard.path))
      }
    })
  }

  const handleDocSaved = (doc: Doc) => {
    toast('Saved to Documents', {
      description: `/${doc.path}`,
      action: {
        label: 'Open document',
        onClick: () => navigate(docsHubDocumentHref(doc.path))
      }
    })
  }

  const handleMakeLive = async () => {
    setSaveOpen(false)
    try {
      const drafted = await dispatch(draftMakeLiveTurn(artifact.conversation_id))
      if (!drafted) {
        toast.error('You can view this conversation but not post in it.')
        return
      }
      toast('Prompt drafted in chat sidebar', {
        description: 'Open the chat sidebar on the right to review and send.',
        position: 'top-center'
      })
    } catch {
      toast.error('Could not draft the prompt in this conversation.')
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteChatArtifact({
        conversationId: artifact.conversation_id,
        artifactId: artifact.id
      }).unwrap()
      setDeleteOpen(false)
      navigate(chatFullPagePath(artifact.conversation_id))
    } catch (caught) {
      const status =
        typeof caught === 'object' && caught !== null && 'status' in caught
          ? (caught as { status?: unknown }).status
          : undefined
      if (status === 403) {
        toast.error(
          "You can view this conversation but not change it, so its artifacts can't be deleted."
        )
      } else {
        toast.error(readMutationError(caught))
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className={cn(
          ARTIFACT_PAGE_MEASURE,
          'flex min-h-10 shrink-0 items-center gap-3 border-b border-border py-1 pb-4'
        )}
      >
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 h-7 px-2 text-xs text-muted-foreground"
        >
          <RouterLink to={chatFullPagePath(artifact.conversation_id)}>
            <ArrowLeft className="size-3.5" aria-hidden />
            Back to conversation
          </RouterLink>
        </Button>
        <h1 className="m-0 min-w-0 truncate text-17 font-semibold tracking-tight text-foreground">
          {artifact.title}
        </h1>
        <Badge variant="secondary">{ARTIFACT_BADGE_LABEL}</Badge>
        {!isDocument && artifact.fetch_mode === 'live' ? (
          <Badge variant="secondary">Live</Badge>
        ) : null}
        <div className="flex-1" />
        {!hasExport && documentBody?.kind !== 'deleted' ? (
          <Button size="xs" className="border border-transparent" onClick={() => setSaveOpen(true)}>
            {isDocument ? 'Save to Documents' : 'Save to Dashboards'}
          </Button>
        ) : exportedLink ? (
          <Button asChild size="xs" className="border border-transparent">
            <RouterLink to={exportedLink.href}>
              <ExternalLink aria-hidden />
              {exportedLink.label}
            </RouterLink>
          </Button>
        ) : null}
        {isDocument ? <DocumentArtifactDownloadButton artifact={artifact} /> : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon-xs" aria-label="Artifact actions">
              <MoreHorizontal aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canCopy ? (
              <DropdownMenuItem onSelect={() => void copyCode()}>
                <Copy aria-hidden />
                Copy {copyLabel}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}>
              <Trash2 aria-hidden />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {documentBody?.kind === 'file' ? (
        <div className={cn(ARTIFACT_PAGE_MEASURE, 'min-h-0 min-w-0 flex-1 overflow-hidden py-4')}>
          <DocFilePreview
            docId={documentBody.documentId}
            revisionId={documentBody.revisionId}
            name={artifact.title}
            format={documentBody.format}
          />
        </div>
      ) : documentBody?.kind === 'deleted' ? (
        <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-sm text-ink-3">
          {DELETED_DOCUMENT_MESSAGE}
        </div>
      ) : documentBody ? (
        <div className="min-h-0 flex-1 overflow-auto">
          <div className={cn(ARTIFACT_PAGE_MEASURE, 'py-5')}>
            <ChatMarkdown className="mx-auto max-w-[76ch] text-sm text-ink-2">
              {documentBody.text}
            </ChatMarkdown>
          </div>
        </div>
      ) : (
        <div className={cn(ARTIFACT_PAGE_MEASURE, 'min-h-0 min-w-0 flex-1 overflow-hidden pt-4')}>
          <SandboxFrame code={artifact.code} templates={templateRegistry} />
        </div>
      )}
      {isDocument ? (
        <SaveToDocsHubDialog
          open={saveOpen}
          artifact={artifact}
          onClose={() => setSaveOpen(false)}
          onSaved={handleDocSaved}
        />
      ) : (
        <SaveToDashboardsDialog
          open={saveOpen}
          artifact={artifact}
          canMakeLive={canMakeLive}
          onClose={() => setSaveOpen(false)}
          onSaved={handleSaved}
          onMakeLive={() => void handleMakeLive()}
        />
      )}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!deleting) setDeleteOpen(next)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete artifact?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{artifact.title}</strong> from the conversation. A{' '}
              {isDocument ? 'document' : 'dashboard'} you saved from it is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault()
                void handleDelete()
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
